import { useEffect, useState } from "react";
import { Form, Skeleton, Select, TextArea } from "../../components/ui";
import { useAddPost, useGetPostById, useUpdatePost } from "../../query/postQuery";
import { useGetAllTeachers } from "../../query/teacherQuery";
import { useGetAllClasses } from "../../query/classQuery";
import { useGetAllChildren } from "../../query/childQuery";
import { useGetAllActivities } from "../../query/activityQuery";
import swal from "../../utils/swal";
import { useNavigate, useParams } from "react-router-dom";
import { UPLOADS_URL } from "../../config/constants";
import { ImagePlus, X } from "lucide-react";
import { POST_TYPES } from "./postHelpers";
import "../teachers/addTeacher.css";
import "./posts.css";

function teacherLabel(teacher) {
  return `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher";
}

function childLabel(child) {
  return `${child.firstName || ""} ${child.lastName || ""}`.trim() || "Student";
}

function AddPost() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const postType = Form.useWatch("type", form) || "CHILD";
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedChildren, setSelectedChildren] = useState([]);

  const { mutate: addPost, isPending: isAdding } = useAddPost();
  const { mutate: updatePost, isPending: isUpdating } = useUpdatePost();
  const { data: post, isLoading } = useGetPostById(isEdit ? id : undefined);
  const { data: teachersData, isLoading: loadingTeachers } = useGetAllTeachers({ page: 1, limit: 200 });
  const { data: classesData, isLoading: loadingClasses } = useGetAllClasses({ page: 1, limit: 200 });
  const { data: childrenData, isLoading: loadingChildren } = useGetAllChildren({ page: 1, limit: 200 });
  const { data: activitiesData, isLoading: loadingActivities } = useGetAllActivities({ page: 1, limit: 200 });

  const teachers = teachersData?.docs || [];
  const classrooms = classesData?.docs || [];
  const children = childrenData?.docs || [];
  const activities = activitiesData?.docs || [];
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !post) return;
    form.setFieldsValue({
      content: post.content || "",
      type: post.type || "CHILD",
      author: post.author?._id || post.author || "",
      activity: post.activity?._id || post.activity || "",
      classroom: post.classroom?._id || post.classroom || "",
    });
    setExistingImages(post.images || []);
    const childIds = (post.children || []).map((item) => (typeof item === "object" ? item._id : item));
    setSelectedChildren(childIds.filter(Boolean));
  }, [isEdit, post, form]);

  const toggleChild = (childId) => {
    setSelectedChildren((prev) =>
      prev.includes(childId) ? prev.filter((item) => item !== childId) : [...prev, childId]
    );
  };

  const addImages = (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file.type?.startsWith("image/"));
    if (!files.length) {
      if (fileList?.length) swal("Oops!", "Please select image files only", "error");
      return;
    }
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (filename) => {
    setExistingImages((prev) => prev.filter((item) => item !== filename));
  };

  const buildFormData = (values) => {
    const formObject = new FormData();
    formObject.append("content", values.content || "");
    formObject.append("type", values.type);
    formObject.append("activity", values.activity);
    formObject.append("author", values.author);

    if (values.type === "CLASS") {
      formObject.append("classroom", values.classroom || "");
    } else {
      formObject.append("children", JSON.stringify(selectedChildren));
    }

    imageFiles.forEach((file) => formObject.append("image", file));

    if (isEdit) {
      const removed = (post?.images || []).filter((img) => !existingImages.includes(img));
      formObject.append("oldImages", JSON.stringify(removed));
    }

    return formObject;
  };

  const onFinish = (values) => {
    if (!values.author) {
      swal("Oops!", "Please select an author teacher", "error");
      return;
    }
    if (values.type === "CHILD" && !selectedChildren.length) {
      swal("Oops!", "Please select at least one student", "error");
      return;
    }
    if (values.type === "CLASS" && !values.classroom) {
      swal("Oops!", "Please select a classroom", "error");
      return;
    }

    const formObject = buildFormData(values);

    if (isEdit) {
      updatePost(
        { id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Post updated successfully", "success");
            navigate("/posts");
          },
          onError: (error) => swal("Error", error?.message || "Failed to update post", "error"),
        }
      );
      return;
    }

    addPost(
      { data: formObject },
      {
        onSuccess: () => {
          swal("Success", "Post created successfully", "success");
          navigate("/posts");
        },
        onError: (error) => swal("Error", error?.message || "Failed to create post", "error"),
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" initialValues={{ type: "CHILD" }}>
        <section className="tf-card">
          <h2 className="tf-card-head">{isEdit ? "Edit Post" : "New Post"}</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">
                Content <span>*</span>
              </p>
              <Form.Item
                name="content"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter content" }]}
              >
                <TextArea rows={5} placeholder="Write post content..." />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Type <span>*</span>
              </p>
              <Form.Item
                name="type"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select placeholder="Select type" options={POST_TYPES} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Author (Teacher) <span>*</span>
              </p>
              <Form.Item
                name="author"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select author" }]}
              >
                <Select
                  placeholder="Select teacher"
                  loading={loadingTeachers}
                  options={teachers.map((teacher) => ({
                    value: teacher._id,
                    label: teacherLabel(teacher),
                  }))}
                />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Activity <span>*</span>
              </p>
              <Form.Item
                name="activity"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select activity" }]}
              >
                <Select
                  placeholder="Select activity"
                  loading={loadingActivities}
                  options={activities.map((activity) => ({
                    value: activity._id,
                    label: activity.title || "Activity",
                  }))}
                />
              </Form.Item>
            </div>

            {postType === "CLASS" ? (
              <div className="tf-field">
                <p className="tf-label">
                  Classroom <span>*</span>
                </p>
                <Form.Item
                  name="classroom"
                  className="tf-item mb-0"
                  rules={[{ required: true, message: "Please select classroom" }]}
                >
                  <Select
                    placeholder="Select classroom"
                    loading={loadingClasses}
                    options={classrooms.map((room) => ({
                      value: room._id,
                      label: room.name || room.className || "Classroom",
                    }))}
                  />
                </Form.Item>
              </div>
            ) : (
              <div className="tf-field">
                <p className="tf-label">
                  Students <span>*</span>
                </p>
                <div className="post-child-checklist">
                  {loadingChildren ? (
                    <span>Selecting students...</span>
                  ) : children.length ? (
                    children.map((child) => (
                      <label key={child._id} className="post-child-check">
                        <input
                          type="checkbox"
                          checked={selectedChildren.includes(child._id)}
                          onChange={() => toggleChild(child._id)}
                        />
                        <span>{childLabel(child)}</span>
                      </label>
                    ))
                  ) : (
                    <span>No students available</span>
                  )}
                </div>
              </div>
            )}

            <div className="tf-field">
              <p className="tf-label">Images</p>
              <div className="post-image-inputs">
                <label className="tf-photo" style={{ minHeight: 100 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    multiple
                    onChange={(event) => {
                      addImages(event.target.files);
                      event.target.value = "";
                    }}
                  />
                  <span className="tf-photo-text">
                    <ImagePlus size={18} style={{ display: "inline", marginRight: 6 }} />
                    Add images (multiple)
                  </span>
                </label>
                <div className="post-image-list">
                  {existingImages.map((filename) => (
                    <div key={filename} className="post-image-preview">
                      <img src={`${UPLOADS_URL}/${filename}`} alt="Existing" />
                      <button
                        type="button"
                        className="post-image-remove"
                        aria-label="Remove image"
                        onClick={() => removeExistingImage(filename)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {imageFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="post-image-preview">
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                      <button
                        type="button"
                        className="post-image-remove"
                        aria-label="Remove image"
                        onClick={() => removeNewImage(index)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/posts")}>
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating}>
            {isMutating ? "Saving..." : isEdit ? "Save" : "Create Post"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddPost;
